<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import type { IEvent } from '~/types/IEvent';
import type { IFile } from "~/types";

interface CategoryOption {
  id: string;
  name: string;
  color: string;
}

interface IEventWithReservation extends IEvent {
  totalPlacesReserved?: number;
  isFull?: boolean;
}

const selectedCategory = ref<CategoryOption | null>(null);
const currentPage = ref(1);
const totalEvents = ref(0);
const { status } = useAuth();
const totalPages = computed(() => Math.ceil(totalEvents.value / 10));
const reservationSuccess = ref(false);
const toast = useToast();
const runtimeConfig = useRuntimeConfig();
const folderLink = runtimeConfig.public.folderLink;

// Map pour stocker les images de chaque événement
const eventImages = ref<Map<string, IFile[]>>(new Map());

const categoryOptions: CategoryOption[] = [
  { id: 'general', name: 'General', color: '808080' },
  { id: 'cultural', name: 'Culturel', color: 'f8f853' },
  { id: 'gastronomy', name: 'Gastronomie', color: '3ba9fa' },
  { id: 'nature', name: 'Nature', color: '14b127' },
  { id: 'workshop', name: 'Atelier', color: 'f80101' },
  { id: 'specialties', name: 'Bien-être', color: 'fa8c3b' },
  { id: 'unusual-places', name: 'Lieux Insolites', color: 'ce29e5' },
  { id: 'short-stays', name: 'Courts Séjours', color: '2563eb' },
];

const { data, pending, error, refresh } = await useAsyncData<{ total: number; events: IEvent[] }>(
  () => $fetch('/api/events/getAvailableEvents', {
    method: 'GET',
    params: {
      category: selectedCategory.value?.id,
      page: currentPage.value
    }
  }),
  {
    watch: [currentPage, selectedCategory]
  }
);

const events = computed<IEvent[]>(() => data.value?.events || []);

// Récupérer les informations de disponibilité pour tous les événements
const { data: eventsWithAvailability } = await useAsyncData('eventsWithAvailability', async () => {
  const currentEvents = events.value;
  if (currentEvents.length === 0) return [];
  
  return await checkEventAvailability(currentEvents);
}, {
  watch: [events]
});

watch(data, (newData) => {
  if (newData && typeof newData.total === 'number') {
    totalEvents.value = newData.total;
  }
}, { immediate: true });

function formatDate(dateString: string | Date) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(date);
}

function formatDateWithTime(dateString: string | Date) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
}

function truncateText(text: string, maxLength: number) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

function filterByCategory(category: string) {
  selectedCategory.value = categoryOptions.find(c => c.id === category) || null;
  currentPage.value = 1;
}

function resetFilter() {
  selectedCategory.value = null;
  currentPage.value = 1;
}

async function checkEventAvailability(events: IEvent[]) {
  const eventsWithReservation: IEventWithReservation[] = [];
  
  for (const event of events) {
    try {
      const { data: reservationData } = await useFetch(`/api/booking/getTotalReservationsByEvent`, {
        method: "GET",
        params: {
          eventId: event._id
        }
      });
      
      const eventWithReservation: IEventWithReservation = {
        ...event,
        totalPlacesReserved: reservationData.value?.totalPlacesReserved || 0,
        isFull: false
      };
      
      if (event.availableSpace && (eventWithReservation.totalPlacesReserved || 0) >= event.availableSpace) {
        eventWithReservation.isFull = true;
      }
      
      eventsWithReservation.push(eventWithReservation);
    } catch (error) {
      const { handleApiError } = useErrorHandler()
      handleApiError(error, 'vérification des places pour l\'événement')
      eventsWithReservation.push({ ...event, totalPlacesReserved: 0, isFull: false });
    }
  }
  
  return eventsWithReservation;
}

async function fetchEventImages(eventId: string) {
  try {
    const { data: items } = await useFetch<IFile[]>(`/api/upload/${eventId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      onResponseError({ response }) {
        const { handleApiError } = useErrorHandler()
        handleApiError({ statusCode: response.status, statusText: response.statusText }, 'chargement des images')
      }
    });

    if (items.value) {
      // Filtrer pour exclure les fichiers non-image
      const filteredItems = items.value.filter(file => {
        const extensions = ['.pdf', '.mp4', '.mov', '.avi'];
        return !extensions.some(ext => file.path.endsWith(ext));
      });
      eventImages.value.set(eventId, filteredItems);
    }
  } catch (error) {
    const { handleApiError } = useErrorHandler()
    handleApiError(error, 'chargement des images pour l\'événement')
  }
}

function getFilteredImages(eventId: string): IFile[] {
  return eventImages.value.get(eventId) || [];
}

function canBookEvent(event: IEventWithReservation): boolean {
  const now = new Date();
  const openingDate = new Date(event.openingDate);
  const closingDate = new Date(event.closingDate);
  
  if (event.isFull) {
    return false;
  }
  
  if (event.availableSpace !== null && event.availableSpace !== undefined) {
    const reservedPlaces = event.totalPlacesReserved || 0;
    if (reservedPlaces >= event.availableSpace) {
      return false;
    }
  }
  
  return openingDate <= now && now <= closingDate;
}

function goToReservation(eventId: string) {
  const event = eventsWithAvailability.value?.find(e => e._id === eventId) as IEventWithReservation;
  if (event && canBookEvent(event)) {
    navigateTo(`/admin/join-event/${eventId}`);
  } else {
    toast.add({
      id: "error",
      color: "red",
      icon: "i-heroicons-exclamation-circle",
      title: "Erreur",
      description: "Cet événement n'est plus disponible pour réservation.",
    });
  }
}

onMounted(() => {
  const route = useRoute();
  if (route.query.reservationSuccess === 'true') {
    reservationSuccess.value = true;
    navigateTo('/events', { replace: true });
    setTimeout(() => {
      reservationSuccess.value = false;
    }, 5000);
  }
});

watch(eventsWithAvailability, async (newEvents) => {
  if (newEvents && newEvents.length > 0) {
    for (const event of newEvents) {
      await fetchEventImages(event._id);
    }
  }
}, { immediate: true });
</script>

<template>
  <div class="container px-6 pt-10 pb-24 mx-auto max-w-7xl sm:pb-32 lg:px-8 lg:py-40 sm:mt-32 lg:mt-4">
    <h1 class="mb-8 text-4xl font-bold text-center">Nos Sorties et Événements</h1>
    
    <UAlert
      v-if="reservationSuccess"
      icon="i-heroicons-check-circle"
      color="green"
      variant="soft"
      title="Réservation confirmée !"
      class="mb-6"
      :ui="{ rounded: 'rounded-xl' }"
    >
      <p>Votre réservation a été enregistrée avec succès. Vous recevrez prochainement un email de confirmation.</p>
      <template #action>
        <UButton color="white" variant="ghost" size="xs" @click="reservationSuccess = false">
          Fermer
        </UButton>
      </template>
    </UAlert>
    
    <!-- Filtre par catégorie -->
    <div class="mb-8">
      <div class="flex flex-wrap items-center gap-3">
        <span class="font-semibold">Filtrer par catégorie:</span>
        <UButton
          v-for="cat in categoryOptions"
          :key="cat.id"
          :color="selectedCategory && selectedCategory.id === cat.id ? 'primary' : 'gray'"
          variant="soft"
          @click="filterByCategory(cat.id)"
          class="text-sm"
        >
          <div class="flex items-center">
            <span 
              class="flex-shrink-0 w-3 h-3 mt-px mr-2 rounded-full" 
              :style="{ background: `#${cat.color}` }" 
            />
            <span>{{ cat.name }}</span>
          </div>
        </UButton>
        <UButton
          v-if="selectedCategory"
          icon="i-heroicons-x-mark"
          color="gray"
          variant="soft"
          @click="resetFilter"
          class="ml-2"
          size="sm"
        >
          Réinitialiser
        </UButton>
      </div>
    </div>
    
    <div v-if="pending" class="flex items-center justify-center py-12">
      <UIcon name="i-heroicons-arrow-path" class="w-8 h-8 animate-spin text-primary" />
      <span class="ml-2">Chargement des activités...</span>
    </div>
    
    <div v-else-if="error" class="relative px-4 py-3 mb-6 text-red-700 bg-red-100 border border-red-400 rounded">
      <strong class="font-bold">Erreur!</strong>
      <span class="block sm:inline"> Impossible de charger les activités. Veuillez réessayer plus tard.</span>
    </div>
    
    <div v-else-if="!eventsWithAvailability || eventsWithAvailability.length === 0" class="py-12 text-center">
      <UIcon name="i-heroicons-calendar-days" class="w-16 h-16 mx-auto mb-4 text-gray-400" />
      <h3 class="mb-2 text-2xl font-semibold">Aucune activité disponible</h3>
      <p class="mb-6 text-gray-600">
        {{ selectedCategory ? "Aucune activité dans cette catégorie pour le moment." : "Il n'y a pas d'activités disponibles actuellement." }}
      </p>
      <UButton v-if="selectedCategory" @click="resetFilter" color="primary">
        Voir toutes les activités
      </UButton>
    </div>
    
    <div v-else class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      <UCard
        v-for="event in eventsWithAvailability"
        :key="event._id"
        class="flex flex-col h-full transition-shadow duration-300 hover:shadow-lg"
      >
        <template #header>
          <div class="relative">
            <div v-if="getFilteredImages(event._id).length === 0" class="flex items-center justify-center w-full h-48 bg-gray-200">
              <img
                src="/assets/logo-background.webp"
                alt="Image par défaut"
                class="object-cover w-full h-full"
                draggable="false"
              />
            </div>

            <UCarousel
              v-else
              v-slot="{ item }"
              :items="getFilteredImages(event._id)"
              :ui="{ item: 'basis-full flex flex-none snap-center' }"
              class="relative w-full h-48 overflow-hidden rounded-lg" 
              arrows
            >
              <img
                :src="folderLink + item.path"
                class="object-cover w-full h-full"
                draggable="false"
              />
            </UCarousel>
            
            <UBadge
              :color="
                event.categorie === 'cultural' ? 'yellow' :
                event.categorie === 'gastronomy' ? 'blue' :
                event.categorie === 'nature' ? 'green' :
                event.categorie === 'workshop' ? 'red' :
                event.categorie === 'specialties' ? 'orange' :
                event.categorie === 'unusual-places' ? 'purple' :
                event.categorie === 'short-stays' ? 'indigo' :
                'gray'
              "
              class="absolute top-2 right-2"
              variant="solid"
            >
              <span v-if="event.categorie === 'cultural'">Culturel</span>
              <span v-else-if="event.categorie === 'gastronomy'">Gastronomie</span>
              <span v-else-if="event.categorie === 'nature'">Nature</span>
              <span v-else-if="event.categorie === 'workshop'">Atelier</span>
              <span v-else-if="event.categorie === 'specialties'">Bien-être</span>
              <span v-else-if="event.categorie === 'unusual-places'">Lieux Insolites</span>
              <span v-else-if="event.categorie === 'short-stays'">Courts Séjours</span>
              <span v-else>Général</span>
            </UBadge>
          </div>
        </template>
        
        <div class="flex-grow">
          <h3 class="mb-2 text-xl font-bold">{{ event.title }}</h3>
          
          <div class="mb-4 space-y-1 text-sm text-gray-600">
            <div class="flex items-start">
              <UIcon name="i-heroicons-calendar" class="mr-2 mt-0.5 flex-shrink-0" />
              <span>
                {{ formatDateWithTime(event.beginDate) }}
                <span v-if="event.endDate">
                  — {{ formatDateWithTime(event.endDate) }}
                </span>
              </span>
            </div>
            
            <div class="flex items-start">
              <UIcon name="i-heroicons-map-pin" class="mr-2 mt-0.5 flex-shrink-0" />
              <span>{{ event.location }}</span>
            </div>
            
            <div v-if="event.availableSpace !== null && event.availableSpace !== undefined" class="flex items-start">
              <UIcon name="i-heroicons-user-group" class="mr-2 mt-0.5 flex-shrink-0" />
              <span>
                {{ event.availableSpace - (event.totalPlacesReserved || 0) }} places disponibles
                <span v-if="event.isFull" class="ml-2 text-red-600 font-semibold">(COMPLET)</span>
              </span>
            </div>
          </div>
          
          <p class="mb-4 text-gray-700">
            {{ event.description ? truncateText(event.description, 100) : "Aucune description disponible." }}
          </p>
          
          <div class="flex flex-wrap gap-3 mb-4">
            <UBadge v-if="event.pietonPrice" color="indigo" variant="soft" class="text-sm">
              Solo: {{ event.pietonPrice }}€
            </UBadge>
            <UBadge v-if="event.driverPrice" color="yellow" variant="soft" class="text-sm">
              Covoituré(e): {{ event.driverPrice }}€
            </UBadge>
          </div>
        </div>
        
        <template #footer>
          <div class="flex items-center justify-between">
            <span class="text-xs text-gray-500">
              Inscription jusqu'au {{ formatDate(event.closingDate) }}
            </span>
            <UButton
              :color="event.isFull ? 'gray' : 'primary'"
              @click="goToReservation(event._id)"
              icon="i-heroicons-ticket"
              size="sm"
              :disabled="event.isFull"
            >
              {{ event.isFull ? 'COMPLET' : 'Réserver' }}
            </UButton>
          </div>
        </template>
      </UCard>
    </div>
    
    <div v-if="totalPages > 1" class="flex justify-center mt-8">
      <UPagination
        v-model="currentPage"
        :total="totalEvents"
        :page-count="totalPages"
        :ui="{ wrapper: 'flex items-center gap-1' }"
      />
    </div>
  </div>
</template>

<style>
.custom-card-transition {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.custom-card-transition:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
</style>